// SEN invigilation -- the special-room half of an exam sitting.
//
// This is deliberately an EXTENSION of invigilation_session rather than a parallel timetable. A
// special room is not another exam: it is the same paper, at the same time, sat by a handful of
// students who hold an accommodation. Hanging it off the parent session means the date, subject,
// paper and start time have exactly one home -- move the exam and the special room moves with it,
// because it never held its own copy of when the exam is.
//
// What a special room DOES own is what makes it different: its venue, its own invigilators, its own
// end time (extra time pushes it past the main hall's), and who is in it.
//
// TWO KINDS OF DATA, TWO DIFFERENT SENSITIVITIES. The register -- which student holds which
// accommodation -- is confidential; the room roster is a duty roster like any other, and the
// invigilator standing in that room has to know who should be in front of them. The rules draw the
// line between the two collections, because rules decide documents and never fields.

/** House defaults for a Hong Kong school. Editable per school in the module's Settings tab. */
export const DEFAULT_ACCOMMODATIONS = [
  { code: 'extra_time', label: 'Extra time' },
  { code: 'separate_room', label: 'Separate room' },
  { code: 'reader', label: 'Reader' },
  { code: 'scribe', label: 'Scribe' },
  { code: 'enlarged_paper', label: 'Enlarged paper' },
  { code: 'special_seating', label: 'Special seating' },
  { code: 'supervised_break', label: 'Supervised break' },
  { code: 'assistive_device', label: 'Assistive device' },
];

/**
 * Extra time is recorded in MINUTES, not as a percentage of the paper.
 *
 * A percentage sounds tidier and is how the entitlement is often written up, but it means the
 * school cannot answer "when does this room finish" without first knowing how long the paper is --
 * and it makes the same student finish at a different offset for every paper they sit. Minutes are
 * what actually goes on the invigilator's sheet.
 *
 * The consequence is intended: a special room finishes AFTER the main hall, by exactly this many
 * minutes, and that is normal rather than something to flag.
 *
 * No default. Zero means nobody has set it, which is the honest starting state for something that
 * changes when an exam ends -- a helpful-looking default here would quietly grant time nobody
 * asked for.
 */
export const DEFAULT_EXTRA_MINUTES = 0;

const pad2 = (n) => String(n).padStart(2, '0');

export const timeToMinutes = (hhmm) => {
  const parts = String(hhmm || '').trim().split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export const minutesToTime = (mins) => {
  if (mins === null || mins === undefined || Number.isNaN(Number(mins))) return '';
  const total = ((Number(mins) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

/** A grant, cleaned up. Negative or unparseable means none -- never a shorter paper. */
export const cleanExtraMinutes = (mins) => {
  const n = Number(mins);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * A special room's own duration and end time, derived from the parent session.
 *
 * `startTime` is the parent's -- a special room starts with everyone else. Only the finish moves,
 * and it moves later. A room ending after the main hall is the point of the room.
 */
export const roomTiming = ({ startTime, duration, extraMinutes }) => {
  const base = Number(duration) || 0;
  const extra = cleanExtraMinutes(extraMinutes);
  const total = base + extra;
  const startMin = timeToMinutes(startTime);
  return {
    duration: total,
    extraMinutes: extra,
    endTime: startMin === null ? '' : minutesToTime(startMin + total),
  };
};

/**
 * The longest grant in the room, because a room runs to its longest-entitled student.
 *
 * Splitting a room by entitlement is the school's call to make when it builds the room, not
 * something to infer here -- so once a set of students is in one room together, they all sit for
 * the longest grant in it. Returns 0 for a room with nobody in it yet.
 */
export const roomExtraMinutes = (students) => (
  (students || []).reduce((max, s) => Math.max(max, cleanExtraMinutes(s?.extraMinutes)), 0)
);

/** Sorted the way a roll is read: class ascending, then class number. */
export const byClassThenNumber = (a, b) => (
  String(a.currentClass || '').localeCompare(String(b.currentClass || ''), undefined, { numeric: true })
  || (Number(a.classNumber || 0) - Number(b.classNumber || 0))
  || String(a.name || '').localeCompare(String(b.name || ''))
);

/** Sessions in the order an exam week runs. */
export const bySessionOrder = (a, b) => (
  String(a.date || '').localeCompare(String(b.date || ''))
  || String(a.startTime || '').localeCompare(String(b.startTime || ''))
  || String(a.subject || '').localeCompare(String(b.subject || ''))
  || String(a.class || '').localeCompare(String(b.class || ''), undefined, { numeric: true })
);

/**
 * Which registered SEN students sit a given parent session.
 *
 * Matched on the class the session is for. A session carries one class, and a student carries the
 * class they are in now -- so this is the whole of the relationship, and it stays true when a
 * student changes class, because neither side stored a copy of the other.
 */
export const senStudentsForSession = (session, register) => (
  (register || [])
    .filter(s => s.active !== false && s.currentClass && s.currentClass === session?.class)
    .sort(byClassThenNumber)
);

/**
 * Students in this session's register who are not yet placed in any of its special rooms.
 *
 * This is the number that matters on the arrangements screen: a SEN student nobody put in a room
 * sits the paper in the main hall without their accommodation, and no error is raised anywhere --
 * so the screen has to be the thing that notices.
 */
export const unplacedStudents = (session, register, rooms) => {
  const placed = placedEmails(rooms);
  return senStudentsForSession(session, register).filter(s => !placed.has(String(s.email).toLowerCase()));
};

/**
 * Every address already sitting in one of these rooms.
 *
 * Read off `students`, the room's own denormalised list, rather than the register -- a room is
 * handed to an invigilator who cannot read the register at all (SEN status is confidential and the
 * rules stop at the collection boundary), so the room has to carry enough to be read on its own.
 */
export const placedEmails = (rooms) => {
  const placed = new Set();
  (rooms || []).forEach(r => (r.students || []).forEach(s => placed.add(String(s?.email || '').toLowerCase())));
  placed.delete('');
  return placed;
};

/** True when every accommodation-holding student in the session has a room. */
export const sessionIsCovered = (session, register, rooms) => (
  senStudentsForSession(session, register).length > 0
  && unplacedStudents(session, register, rooms).length === 0
);

/** A label for a room that has no name of its own: the venue, or the students in it. */
export const roomLabel = (room, count) => (
  room?.venue || (count ? `${count} student${count === 1 ? '' : 's'}` : 'Unassigned room')
);

// ── The room's running order ───────────────────────────────────────────────────────────────────
//
// What an invigilator actually needs is not a paragraph of notes, it is a list of things to do at
// particular moments: hand out the stationery at the start, show the white card at 09:18, call
// fifteen minutes remaining at 09:25.
//
// ANCHORED, NOT WRITTEN DOWN AS CLOCK TIMES. "09:25" is really "fifteen minutes before this room
// finishes" -- and this room's finish moves whenever the extra time changes or the exam is
// rescheduled in Invigilation. A list typed as fixed clock times is correct on the day it is
// written and quietly wrong afterwards, with nothing to show that it drifted. So an item says what
// it is anchored to and the clock time is worked out; a fixed time is still available for the cases
// that really are fixed, and it is then the school's own claim rather than a stale derivation.

export const CHECKLIST_ANCHOR = {
  START: 'start', // offset minutes AFTER the paper starts (0 = as it starts)
  END: 'end',     // offset minutes BEFORE this room finishes
  CLOCK: 'clock', // a literal time, for something genuinely tied to the clock
};

export const newChecklistItem = (seq = 0) => ({
  id: `i${seq}`, when: CHECKLIST_ANCHOR.START, offset: 0, at: '', text: '',
});

/** The clock time an item lands on, given this room's own start and finish. '' if unknowable. */
export const resolveChecklistTime = (item, { startTime, endTime } = {}) => {
  if (!item) return '';
  if (item.when === CHECKLIST_ANCHOR.CLOCK) return item.at || '';
  const anchorMin = timeToMinutes(item.when === CHECKLIST_ANCHOR.END ? endTime : startTime);
  if (anchorMin === null) return '';
  const offset = Number(item.offset) || 0;
  const mins = item.when === CHECKLIST_ANCHOR.END ? anchorMin - offset : anchorMin + offset;
  return minutesToTime(mins);
};

/** How an item reads when there is no room to resolve it against -- the Settings template. */
export const describeChecklistItem = (item) => {
  const offset = Number(item?.offset) || 0;
  if (item?.when === CHECKLIST_ANCHOR.CLOCK) return item.at || 'no time set';
  if (item?.when === CHECKLIST_ANCHOR.END) return offset === 0 ? 'at the end' : `${offset} min before the end`;
  return offset === 0 ? 'as it starts' : `${offset} min after the start`;
};

/**
 * The list as the school arranged it, each item stamped with the time it lands on.
 *
 * THE ORDER IS THE SCHOOL'S, not the clock's. Sorting by the computed time looks tidier and is
 * wrong: several steps routinely land on the same minute -- collect the scripts, then dismiss the
 * room, both at the end -- and a clock sort has nothing to order those by, so it would shuffle them
 * arbitrarily on every render. The school drags them into the order they happen, and that is what
 * is stored and shown. `sortChecklistByTime` exists for when they want the clock to decide.
 */
export const resolveChecklist = (items, times) => (
  (items || []).map(item => ({ ...item, time: resolveChecklistTime(item, times) }))
);

/**
 * The same list in clock order, for the button that offers to tidy it up.
 *
 * Stable: items landing on the same minute keep the order the school put them in, and items with no
 * workable time (a fixed time never filled in, an unscheduled paper) go last rather than being
 * dropped -- something the school wrote down must not vanish because we could not place it.
 */
export const sortChecklistByTime = (items, times) => (
  resolveChecklist(items, times)
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      if (!a.item.time && !b.item.time) return a.i - b.i;
      if (!a.item.time) return 1;
      if (!b.item.time) return -1;
      return a.item.time.localeCompare(b.item.time) || (a.i - b.i);
    })
    .map(({ item }) => item)
);

/** True when the stored order does not match the clock -- worth pointing out, not worth fixing. */
export const checklistIsOutOfOrder = (items, times) => {
  const resolved = resolveChecklist(items, times).filter(i => i.time);
  return resolved.some((it, i) => i > 0 && it.time < resolved[i - 1].time);
};

/** Move one item to another position, the operation a drag actually performs. */
export const moveChecklistItem = (items, from, to) => {
  const list = [...(items || [])];
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
};

/** Drops the blank rows an editor always leaves behind. An item with no instruction is not one. */
export const cleanChecklist = (items) => (
  (items || [])
    .filter(i => String(i?.text || '').trim())
    .map(i => ({
      id: i.id,
      when: i.when || CHECKLIST_ANCHOR.START,
      offset: i.when === CHECKLIST_ANCHOR.CLOCK ? 0 : Math.max(0, Math.round(Number(i.offset) || 0)),
      at: i.when === CHECKLIST_ANCHOR.CLOCK ? (i.at || '') : '',
      text: String(i.text).trim(),
    }))
);

/** What a school starts from, and roughly what the examples in the brief described. */
export const DEFAULT_CHECKLIST = [
  { id: 'd1', when: CHECKLIST_ANCHOR.START, offset: 0, at: '', text: 'Hand out the specified stationery' },
  { id: 'd2', when: CHECKLIST_ANCHOR.END, offset: 15, at: '', text: 'Tell the students 15 minutes remain' },
  { id: 'd3', when: CHECKLIST_ANCHOR.END, offset: 0, at: '', text: 'Stop writing, collect the scripts' },
];

// ── Importing the school's own register ────────────────────────────────────────────────────────
//
// Schools already keep this list, usually in a spreadsheet built long before the platform existed.
// Retyping it is the sort of task that gets done once and never updated, so the import has to
// tolerate the file the school actually has rather than demand the one we would have designed.
//
// What it will NOT do is invent a student. The register is keyed by address, and a row that names
// somebody the roll does not know is reported rather than created -- a typo in a student number
// must not quietly become a second, permanent, unreachable record.

/** Header matching that survives spacing, capitals, punctuation and a stray BOM. */
const normHeader = (h) => String(h || '').replace(/^\uFEFF/, '').toLowerCase().replace(/[^a-z0-9一-鿿]/g, '');

const HEADER_ALIASES = {
  sid: ['sid', 'studentid', 'studentno', 'studentnumber', 'regno', '學號', '學生編號'],
  email: ['email', 'emailaddress', 'account', '電郵'],
  extraMinutes: ['extraminutes', 'extramins', 'extratime', 'extratimeminutes', 'minutes', '加時', '加時分鐘'],
  notes: ['notes', 'note', 'remarks', 'remark', 'comment', 'comments', '備註'],
  accommodations: ['accommodations', 'accommodation', 'arrangements', 'arrangement', 'sen', '調適', '特殊安排'],
  active: ['active', 'onregister', 'current', '在冊'],
};

const findColumn = (headers, aliases) => headers.find(h => aliases.includes(normHeader(h)));

/** Y / Yes / 1 / true / X / a tick all mean the same thing in a spreadsheet built by hand. */
const isAffirmative = (v) => ['y', 'yes', '1', 'true', 't', 'x', '✓', '✔', 'v', '是', '有'].includes(
  String(v ?? '').trim().toLowerCase(),
);

const isNegative = (v) => {
  const s = String(v ?? '').trim().toLowerCase();
  return s === '' || ['n', 'no', '0', 'false', 'f', '-', '否', '冇'].includes(s);
};

/**
 * Turn parsed CSV rows into register entries, resolving each to a real student.
 *
 * `students` is the roll: `{ email, sid, name, currentClass, classNumber }`. `accommodations` is
 * the school's own vocabulary, so a column headed with either the code or the visible label counts.
 *
 * Returns `{ entries, problems, columns }` -- never throws and never partially applies. The caller
 * shows the problems before writing anything, because an import that silently drops eight rows is
 * worse than one that refuses.
 */
// `t` is optional and defaults to the English it is given: this parser is tested under plain
// node, and the import preview shows these reasons verbatim.
export const parseRegisterCsv = (rows, { students = [], accommodations = [], t = (_k, _v, fb) => fb } = {}) => {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const col = Object.fromEntries(
    Object.entries(HEADER_ALIASES).map(([key, aliases]) => [key, findColumn(headers, aliases)]),
  );

  // A column per accommodation, headed by either its code or its label.
  const accColumns = accommodations
    .map(a => ({ code: a.code, header: headers.find(h => normHeader(h) === normHeader(a.code) || normHeader(h) === normHeader(a.label)) }))
    .filter(a => a.header);

  const accByToken = new Map();
  accommodations.forEach(a => {
    accByToken.set(normHeader(a.code), a.code);
    accByToken.set(normHeader(a.label), a.code);
  });

  const bySid = new Map(students.filter(s => s.sid).map(s => [String(s.sid).trim(), s]));
  const byEmail = new Map(students.map(s => [String(s.email).toLowerCase(), s]));

  const entries = [];
  const problems = [];
  const seen = new Set();

  rows.forEach((row, i) => {
    const line = i + 2; // +1 for the header row, +1 because humans count from one
    const rawSid = col.sid ? String(row[col.sid] ?? '').trim() : '';
    const rawEmail = col.email ? String(row[col.email] ?? '').trim().toLowerCase() : '';
    if (!rawSid && !rawEmail) {
      problems.push({ line, reason: t('senInvigilation.importProblems.noStudentId', {}, 'No student number or address on this row') });
      return;
    }

    const student = (rawEmail && byEmail.get(rawEmail)) || (rawSid && bySid.get(rawSid));
    if (!student) {
      problems.push({ line, who: rawSid || rawEmail, reason: t('senInvigilation.importProblems.notOnRoll', {}, 'Nobody on the roll has that student number or address') });
      return;
    }
    if (seen.has(student.email)) {
      problems.push({ line, who: student.name || student.email, reason: t('senInvigilation.importProblems.duplicateRow', {}, 'Appears more than once in this file') });
      return;
    }
    seen.add(student.email);

    // Codes may arrive as one list column, as a column each, or both -- take the union.
    const codes = new Set();
    if (col.accommodations) {
      String(row[col.accommodations] ?? '')
        .split(/[;,/|]/)
        .map(t => normHeader(t))
        .filter(Boolean)
        .forEach(token => {
          const code = accByToken.get(token);
          if (code) codes.add(code);
          else problems.push({ line, who: student.name, reason: `Unknown accommodation "${token}" -- ignored`, warning: true });
        });
    }
    accColumns.forEach(({ code, header }) => {
      const v = row[header];
      if (isAffirmative(v)) codes.add(code);
      else if (!isNegative(v)) problems.push({ line, who: student.name, reason: `Could not read "${header}" value "${v}" -- treated as no`, warning: true });
    });

    const rawMins = col.extraMinutes ? row[col.extraMinutes] : 0;
    if (col.extraMinutes && String(rawMins ?? '').trim() !== '' && cleanExtraMinutes(rawMins) === 0 && Number(rawMins) !== 0) {
      problems.push({ line, who: student.name, reason: `Could not read extra time "${rawMins}" -- recorded as none`, warning: true });
    }

    entries.push({
      email: student.email,
      sid: student.sid || '',
      name: student.name || '',
      currentClass: student.currentClass || '',
      classNumber: student.classNumber || '',
      accommodations: [...codes],
      extraMinutes: cleanExtraMinutes(rawMins),
      notes: col.notes ? String(row[col.notes] ?? '').trim() : '',
      active: col.active ? !isNegative(row[col.active]) : true,
    });
  });

  return { entries, problems, columns: { ...col, accColumns: accColumns.map(a => a.header) } };
};

/** The header row this import understands, for the downloadable template. */
export const registerCsvTemplate = (accommodations = DEFAULT_ACCOMMODATIONS) => ([
  'SID', 'Name', 'Extra minutes', ...accommodations.map(a => a.label), 'Notes',
]);
